/**
 * Money arrives from the API as a decimal string with two places ("1234567.50"). The UI formats and
 * parses it as text and never adds (spec §15): every total on screen is the server's.
 */

export const moneyLocale = (language: string | undefined): string => (language?.toLowerCase().startsWith('cs') ? 'cs-CZ' : 'en-GB')

const DECIMAL = /^(-?)(\d+)(?:\.(\d+))?$/

export const isZeroMoney = (value: string): boolean => !/[1-9]/.test(value)
export const isNegativeMoney = (value: string): boolean => value.trim().startsWith('-') && !isZeroMoney(value)

/** True when the formatted amount reads as zero, so "-0" can be shown as "0". */
const roundsToZero = (value: string, whole: boolean): boolean => {
  const m = DECIMAL.exec(value.trim())
  if (!m || /[1-9]/.test(m[2])) return false
  const frac = (m[3] ?? '').padEnd(2, '0')
  return whole ? frac[0] < '5' : /^0+$/.test(frac)
}

const formatters = new Map<string, Intl.NumberFormat>()
const formatterFor = (locale: string, currency: string, digits: number): Intl.NumberFormat => {
  const key = `${locale}|${currency}|${digits}`
  let f = formatters.get(key)
  if (!f) {
    try {
      f = new Intl.NumberFormat(locale, { style: 'currency', currency, minimumFractionDigits: digits, maximumFractionDigits: digits })
    } catch {
      f = new Intl.NumberFormat(locale, { minimumFractionDigits: digits, maximumFractionDigits: digits })
    }
    formatters.set(key, f)
  }
  return f
}

/**
 * `whole` for grid cells (whole currency units, half up), otherwise two decimals. The decimal string goes to
 * Intl as is: Intl.NumberFormat formats a numeric string exactly, without a binary-float step.
 */
export const formatMoney = (value: string, opts: { locale: string; currency: string; whole?: boolean }): string => {
  const whole = !!opts.whole
  const text = roundsToZero(value, whole) ? '0' : value
  return formatterFor(opts.locale, opts.currency, whole ? 0 : 2).format(text as unknown as number)
}

export type MoneyParse = { ok: true; value: string | null } | { ok: false }

const CURRENCY_AFFIX = /^(kč|czk|eur|€|usd|\$)|(kč|czk|eur|€|usd|\$)$/gi
const MAX_INTEGER_DIGITS = 13

/**
 * Text a person typed into a money field → decimal string with two places, by the rules of the server's
 * parseMoneyText (src/services/costControl/money.ts): grouping spaces (incl. no-break ones), apostrophes and a
 * currency affix are ignored; with both "," and "." the last one is the decimal separator; a single one of
 * either is decimal; repeated ones are grouping. More than two decimals or more than 13 integer digits is
 * refused, never rounded. Empty text, or a lone dash ('-', '–', '—'), → `value: null`.
 */
export function parseMoneyInput(text: string, opts: { allowNegative?: boolean } = {}): MoneyParse {
  let s = text.replace(/[\s']/g, '').replace(CURRENCY_AFFIX, '')
  if (s === '' || /^[-–—]$/.test(s)) return { ok: true, value: null }
  const commas = (s.match(/,/g) ?? []).length
  const dots = (s.match(/\./g) ?? []).length
  if (commas && dots) {
    const decimal = s.lastIndexOf(',') > s.lastIndexOf('.') ? ',' : '.'
    s = s.split(decimal === ',' ? '.' : ',').join('').replace(decimal, '.')
  } else if (commas) {
    s = commas > 1 ? s.split(',').join('') : s.replace(',', '.')
  } else if (dots > 1) {
    s = s.split('.').join('')
  }
  const m = DECIMAL.exec(s)
  if (!m) return { ok: false }
  const [, sign, digits, frac = ''] = m
  if (frac.length > 2) return { ok: false }
  const int = digits.replace(/^0+(?=\d)/, '')
  if (int.length > MAX_INTEGER_DIGITS) return { ok: false }
  const cents = frac.padEnd(2, '0')
  const zero = int === '0' && cents === '00'
  if (sign && !zero && !opts.allowNegative) return { ok: false }
  return { ok: true, value: `${zero ? '' : sign}${int}.${cents}` }
}

/** A stored amount as editable text: the locale's decimal separator, no grouping, no ",00". */
export const toEditText = (value: string, locale: string): string => {
  const [int, frac] = value.split('.')
  const sep = locale.startsWith('cs') ? ',' : '.'
  return frac && frac !== '00' ? `${int}${sep}${frac}` : int
}
